var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import RangeMin from "./RangeMin";
import RangeMax from "./RangeMax";
import RangeOutput from "./RangeOutput";
import { useMergeRefs } from "../../../../common/useFallbackRef";
import RangeDoubleModel from "./model/RangeDoubleModel";
import RangeModel from "./model/RangeModel";
var Range = forwardRef(function (_a, ref) {
    var _b, _c;
    var idLabel = _a.idLabel, idMessagesGroup = _a.idMessagesGroup, _d = _a.min, min = _d === void 0 ? 0 : _d, _e = _a.max, max = _e === void 0 ? 100 : _e, step = _a.step, _f = _a.size, size = _f === void 0 ? "md" : _f, disabled = _a.disabled, prefix = _a.prefix, suffix = _a.suffix, _g = _a.hideMinMaxIndicator, hideMinMaxIndicator = _g === void 0 ? false : _g, inputProps = _a.inputProps, secondInputProps = _a.secondInputProps, className = _a.className, style = _a.style, otherProps = __rest(_a, ["idLabel", "idMessagesGroup", "min", "max", "step", "size", "disabled", "prefix", "suffix", "hideMinMaxIndicator", "inputProps", "secondInputProps", "className", "style"]);
    var _h = useState((_b = inputProps.defaultValue) !== null && _b !== void 0 ? _b : 0), value = _h[0], setValue = _h[1];
    var _j = useState((_c = (secondInputProps && secondInputProps.defaultValue)) !== null && _c !== void 0 ? _c : 0), secondValue = _j[0], setSecondValue = _j[1];
    var containerRef = useRef(null);
    var _k = useState(undefined), containerWidth = _k[0], setContainerWidth = _k[1];
    var mergedContainerRef = useMergeRefs([ref, containerRef]);
    var rangeModel = useMemo(function () {
        var newRangeModel = new RangeModel(min, max, size === "sm", step, prefix, suffix);
        if (secondInputProps) {
            newRangeModel = new RangeDoubleModel(min, max, size === "sm", step, prefix, suffix, secondValue);
        }
        newRangeModel.value = value;
        newRangeModel.width = containerWidth;
        return newRangeModel;
    }, [secondInputProps, min, max, step, value, secondValue, size, containerWidth]);
    useEffect(function () {
        if (inputProps.value !== undefined) {
            setValue(inputProps.value);
        }
    }, [inputProps.value]);
    useEffect(function () {
        if ((secondInputProps === null || secondInputProps === void 0 ? void 0 : secondInputProps.value) !== undefined) {
            setSecondValue(secondInputProps.value);
        }
    }, [secondInputProps === null || secondInputProps === void 0 ? void 0 : secondInputProps.value]);
    function propsInputOnChange(event) {
        if (inputProps.onChange) {
            inputProps.onChange(event);
        }
    }
    function propsSecondInputOnChange(event) {
        if (secondInputProps && secondInputProps.onChange) {
            secondInputProps.onChange(event);
        }
    }
    var handleChange = function (event) {
        var newValue = parseFloat(event.target.value);
        setValue(newValue);
        propsInputOnChange(event);
        if (secondInputProps && newValue > secondValue) {
            setSecondValue(newValue);
            propsSecondInputOnChange(event);
        }
    };
    var handleSecondChange = function (event) {
        var newValue = parseFloat(event.target.value);
        setSecondValue(newValue);
        propsSecondInputOnChange(event);
        if (newValue < value) {
            setValue(newValue);
            propsInputOnChange(event);
        }
    };
    var handleResize = useCallback(function () {
        if (containerRef && containerRef.current) {
            setContainerWidth(containerRef.current.getBoundingClientRect().width);
        }
    }, [containerRef]);
    useEffect(function () {
        handleResize();
    }, [handleResize]);
    useEffect(function () {
        window.addEventListener("resize", handleResize);
        var resizeObserver = new ResizeObserver(handleResize);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        return function () {
            resizeObserver.disconnect();
            window.removeEventListener("resize", handleResize);
        };
    }, []);
    var rangeClassName = useMemo(function () {
        return rangeModel.getClassName(className);
    }, [rangeModel, className]);
    var rangeStyle = useMemo(function () {
        return __assign(__assign(__assign({}, style), rangeModel.getProgressStyle()), rangeModel.getStepStyle());
    }, [style, rangeModel]);
    return (React.createElement("div", __assign({}, otherProps, { className: rangeClassName, ref: mergedContainerRef, style: rangeStyle, "data-fr-js-range": "true" }),
        React.createElement(RangeOutput, { rangeModel: rangeModel }),
        React.createElement("input", __assign({}, inputProps, { type: "range", min: min, max: max, step: step, disabled: disabled, "aria-labelledby": idLabel, "aria-describedby": idMessagesGroup, onChange: handleChange, defaultValue: undefined, value: value })),
        secondInputProps && (React.createElement("input", __assign({}, secondInputProps, { type: "range", min: min, max: max, step: step, disabled: disabled, "aria-labelledby": idLabel, "aria-describedby": idMessagesGroup, defaultValue: undefined, value: secondValue, onChange: handleSecondChange }))),
        React.createElement(RangeMin, { rangeModel: rangeModel, hide: hideMinMaxIndicator }),
        React.createElement(RangeMax, { rangeModel: rangeModel, hide: hideMinMaxIndicator })));
});
export default Range;
